import { useEffect, useRef } from "react";
import { INBOX_BOOTSTRAP_FOLDER_GAP_MS } from "@/lib/constants/inbox";
import { idleDelay, syncFolderFromServerCache, syncFolderHeaders } from "@/lib/mail/folderWarmup";
import { isOutboxSendInFlight } from "@/lib/outbox/outboxSendLock";

/** Slack court après l’échéance (plus de +70 s — trop lent pour l’UI). */
const DUE_IMAP_GRACE_MS = 800;

type Options = {
  accountId?: number;
  holdFolder?: string | null;
  /** Prochaine échéance snooze ou envoi programmé (ms epoch). */
  nextDueAtMs: number | null;
  /** Comptes à resynchroniser INBOX après échéance (multi-comptes). */
  accountIds: number[];
  enabled?: boolean;
};

/**
 * À l’échéance (cron ~1 min) : resync IMAP INBOX tous comptes + dossier hold.
 * Léger — pas de poll permanent, déclenché par le timer des hooks snooze/scheduled.
 */
export function useMailDueImapRefresh({
  accountId,
  holdFolder,
  nextDueAtMs,
  accountIds,
  enabled = true,
}: Options) {
  const tickRef = useRef<() => void>(() => {});

  tickRef.current = () => {
    if (!enabled || document.visibilityState !== "visible" || isOutboxSendInFlight()) return;

    void (async () => {
      const ids = accountIds.length > 0 ? accountIds : accountId != null ? [accountId] : [];
      for (const id of ids) {
        await syncFolderFromServerCache(id, "INBOX");
        await syncFolderHeaders(id, "INBOX");
        await idleDelay(INBOX_BOOTSTRAP_FOLDER_GAP_MS);
      }
      if (accountId != null && holdFolder?.trim()) {
        await syncFolderHeaders(accountId, holdFolder.trim());
      }
    })();
  };

  useEffect(() => {
    if (!enabled || nextDueAtMs == null) return;

    const now = Date.now();
    const delay =
      nextDueAtMs <= now + 15_000
        ? 2_500
        : Math.max(2000, nextDueAtMs - now + DUE_IMAP_GRACE_MS);
    const timerId = window.setTimeout(() => {
      tickRef.current();
    }, delay);

    return () => window.clearTimeout(timerId);
  }, [enabled, nextDueAtMs, accountId, holdFolder, accountIds.join(",")]);
}
